/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Heading from '@atlaskit/heading/heading';
import ButtonGroup from '@atlaskit/button/button-group';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import type { IllustrationFn } from '@jira/illustrations__illustration/src/ui/types.tsx';

/**
 * Visual appearance of the upsell card.
 *
 * - `standalone` (default): Use when rendering inside a generic surface that
 *   doesn't provide its own chrome (e.g. a `Popup`).
 * - `embedded`: Use when rendering inside a surface that already provides
 *   padding/elevation (e.g. a `FlyoutMenuItemContent`).
 */
export type JiraUpsellCardAppearance = 'standalone' | 'embedded';

export type JiraUpsellCardProps = {
	/** The heading content of the upsell card. */
	heading: React.ReactNode;
	/** The body content of the upsell card. */
	body: React.ReactNode;
	/** The illustration component to display at the top of the card. */
	illustration: IllustrationFn;
	/**
	 * The primary CTA button element. Pass a fully-formed button so the caller
	 * controls the button's lifecycle, analytics, and entry-point wiring.
	 * e.g. `<PremiumUpgradeModalEntryPointButton>Try it free</PremiumUpgradeModalEntryPointButton>`
	 */
	primaryButton: React.ReactNode;
	/**
	 * The secondary button element. Pass a fully-formed button or link so the
	 * caller controls the label, href, analytics, and navigation behaviour.
	 * e.g. `<LinkButton href="..." target="_blank">Learn more</LinkButton>`
	 */
	secondaryButton: React.ReactNode;
	/**
	 * Visual appearance. Defaults to `'standalone'` so existing consumers keep
	 * their current rendering. Set to `'embedded'` when rendering inside a
	 * surface that already provides padding/elevation.
	 */
	appearance?: JiraUpsellCardAppearance;
};

const styles = cssMap({
	// Standalone: self-contained card with its own chrome.
	standaloneCard: {
		width: '400px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		overflow: 'hidden',
	},
	// Embedded: flush, no chrome — relies on the parent surface for padding/elevation.
	embeddedCard: {},
	// Image wrapper for the embedded variant. Two exclusive variants so the
	// radius matches the surrounding ADS popup, which switches between
	// `radius.small` and `radius.large` depending on the
	// `platform-dst-shape-theme-default` flag.
	embeddedImageLegacyRadius: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	embeddedImageShapeRadius: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	// Content padding variants for embedded vs standalone.
	contentPaddingEmbedded: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		paddingInline: token('space.100'),
	},
	contentPaddingStandalone: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInline: token('space.200'),
	},
});

/**
 * An upsell card shown to free Jira users when they discover a paid feature.
 *
 * The card displays an illustration at the top, followed by a heading,
 * description, and two action buttons ("Learn more" and "Try it free").
 *
 * Use one of the named variant components (e.g. `PlansUpsellCard`,
 * `ArchiveUpsellCard`) for pre-configured content, or compose this base
 * component directly with custom props.
 *
 * Set the `appearance` prop to `'embedded'` when rendering inside a surface
 * that already provides padding/elevation (e.g. an ADS `FlyoutMenuItemContent`).
 */
export const JiraUpsellCard = ({
	heading,
	body,
	illustration: Illustration,
	primaryButton,
	secondaryButton,
	appearance = 'standalone',
}: JiraUpsellCardProps): JSX.Element => {
	const isEmbedded = appearance === 'embedded';

	const illustration = isEmbedded ? (
		// Embedded: wrap in a styled Box to apply border + radius matching the
		// surrounding ADS popup (radius switches based on platform-dst-shape-theme-default).
		<Box
			xcss={
				fg('platform-dst-shape-theme-default')
					? styles.embeddedImageShapeRadius
					: styles.embeddedImageLegacyRadius
			}
		>
			<Illustration alt="" height={192} testId="feature-gates.image" />
		</Box>
	) : (
		<Illustration alt="" width={400} height={135} testId="feature-gates.image" />
	);

	return (
		<Box
			xcss={cx(
				appearance === 'embedded' && styles.embeddedCard,
				appearance === 'standalone' && styles.standaloneCard,
			)}
			testId="feature-gates.upsell-card"
		>
			{illustration}
			<Box xcss={isEmbedded ? styles.contentPaddingEmbedded : styles.contentPaddingStandalone}>
				<Stack space={isEmbedded ? 'space.075' : 'space.200'}>
					<Heading size={isEmbedded ? 'xsmall' : 'small'}>{heading}</Heading>
					<Text as="p">{body}</Text>
					<Inline alignBlock="center" alignInline="end">
						<ButtonGroup>
							{secondaryButton}
							{primaryButton}
						</ButtonGroup>
					</Inline>
				</Stack>
			</Box>
		</Box>
	);
};
